import React, { useState, useEffect, useContext, memo, useCallback, createContext } from 'react'
import {
  Box,
  Skeleton,
  IconButton,
  Button,
  Select,
  FormControl,
  MenuItem,
  Alert,
  Tooltip,
  Typography,
} from '@mui/material'
import { DndContext, MouseSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToWindowEdges, snapCenterToCursor } from '@dnd-kit/modifiers'
import InfoIcon from '@mui/icons-material/Info'
import Droppable from './dnd/Droppable'
import classNames from 'classnames'
import { useNotify, useTranslate } from 'react-admin'
import { findKey } from 'src/components/pages/sales/business-rule/helper/findKey'
import { CheckboxContext } from 'src/components/pages/sales/business-rule/logic/CheckboxContext'
import { getNextIndex } from 'src/components/pages/sales/business-rule/helper/vars_helper'
import './style.css'
import { operators } from 'src/components/pages/sales/business-rule/logic/ListOperators'
import DroppableSortable from 'src/components/pages/sales/business-rule/logic/dnd/DroppableSortable'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { LIQUIDATION, PRICE } from '../helper/const'

const COLORS = [
  { color: '#E8C8B3', text: 'ra.legend.show_sitio', showIf: [PRICE] },
  { color: '#E8E3B3', text: 'ra.legend.show_breakdown' },
  { color: '#70b578', text: 'ra.legend.show_global_variable' },
  { color: '#8CAD3C', text: 'ra.legend.show_breakdown_global_variable' },
  { color: '#B3D8E8', text: 'ra.legend.show_breakdown_and_sitio', showIf: [PRICE] },
  { color: '#B2FFFF', text: 'ra.legend.show_liquidation_retail' },
  { color: '#4CBDE3', text: 'ra.legend.show_liquidation_wholesaler' },
  { color: '#D58CDE', text: 'ra.legend.show_liquidation_supplier' },
]

export const DraggingAssignContext = createContext({
  draggingAssign: false,
  setDraggingAssign: () => {},
  isSmall: false,
})

const CollapsibleSection = ({ title, children, isOpen, onToggle }) => (
  <Box>
    <Box display='flex' justifyContent='space-between' alignItems='center'>
      <Box>{title}</Box>
      <IconButton onClick={onToggle}>{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
    </Box>
    {isOpen && <Box>{children}</Box>}
  </Box>
)

export const FormulaComponent = memo(
  ({
    formulas,
    setFormulas,
    initialFormulas,
    onlyView = false,
    formula_applied = undefined,
    withoutVars = false,
    withoutValues = false,
    setOpenjsonFormula,
    loading,
    jsonParse,
    selectedRuleType,
    ruleLiquidationTypes
  }) => {
    const translate = useTranslate()
    const notify = useNotify()
    const [initialLoad, setInitialLoad] = useState(true)
    const [selectedItem, setSelectedItem] = useState()
    const [highlight, setHighlight] = useState({})
    const [draggingAssign, setDraggingAssign] = useState(false)
    const [showOperators, setShowOperators] = useState(true)
    const [showVariables, setShowVariables] = useState(true)
    const [showLegend, setShowLegend] = useState(true)
    const [isSmall, setIsSmall] = useState(false)
    const toggleSize = () => setIsSmall(!isSmall)
    const [currentPage, setCurrentPage] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(100)
    const [showAlert, setShowAlert] = useState(false)
    const totalPages = Math.ceil(formulas.length / itemsPerPage)

    const handleTooltipClick = () => {
      setShowAlert((prev) => !prev)
    }

    const handlePageChange = (pageIndex) => {
      setCurrentPage(pageIndex)
    }

    const paginate = (items, currentPage) => {
      const startIndex = currentPage * itemsPerPage
      return items.slice(startIndex, startIndex + itemsPerPage)
    }

    const handleItemsPerPageChange = (event) => {
      setItemsPerPage(event.target.value)
      setCurrentPage(0) // Reset to the first page whenever items per page change
    }

    const itemsToShow = paginate(formulas, currentPage)
    const mouseSensor = useSensor(MouseSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 999,
      },
    })
    const sensors = useSensors(mouseSensor)
    const context = useContext(CheckboxContext)
    const { checkedBox = [], variablesFormat = [] } = context || {}

    useEffect(() => {
      const checkInitialData = !initialLoad || (initialLoad && checkedBox.length > 0)
      if (formulas?.length > 0 && checkInitialData) {
        let CheckValueFormulas = checkInputValues([...formulas])
        if (formula_applied) {
          updateValuesWithSolvedFormula(CheckValueFormulas, formula_applied)
        }
        setFormulas(CheckValueFormulas)
        setInitialLoad(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoad, checkedBox])

    useEffect(() => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (initialFormulas) {
        transformFormulaStrucure(initialFormulas, variablesFormat, checkedBox, operators, formula_applied)
      }
    }, [initialFormulas, loading])

    const transformFormulaStrucure = async (
      initialFormulas,
      variablesFormat,
      checkedBox,
      operators,
      formula_applied
    ) => {
      if (initialFormulas && variablesFormat.length === checkedBox.length) {
        const transformedStructure = tranformStructure(
          initialFormulas,
          null,
          null,
          getNextIndex([], operators, variablesFormat, formulas),
          operators,
          variablesFormat
        )
        if (formula_applied) {
          updateValuesWithSolvedFormula(transformedStructure[0], formula_applied)
        }
        setFormulas(transformedStructure[0])
      }
    }

    const getValueFromSearch = (position, formula_applied, variable, chosen) => {
      if (chosen == 'var') {
        return isCustomVarVariableRecursive(position, formula_applied, variable)
      }
      if (chosen != 'var') {
        return isDataVarVariableRecursive(position, formula_applied, variable)
      }
    }

    const getValueOf = (position, variable, formula_applied) => {
      // example:
      //  "formula_applied": {
      //    "installmentData": {
      //        "amount": 151596,
      //        "originalAmount": 111112
      //    }
      //  "var": {
      //      extra: {
      //         value: 10,
      //         show: false,
      //         description: ''
      //      }
      //  }
      //  }
      let value = null
      value = isCustomVarVariable(position, variable, formula_applied, value)
      value = isDataVarVariable(position, variable, formula_applied, value)
      return value
    }

    const findPosition = (position, listPosition) => {
      if (listPosition.includes(position)) {
        return position
      }
      // return first number less than position
      return (
        listPosition
          .slice(0, position)
          .reverse()
          .find((x) => listPosition.includes(x)) || null
      )
    }

    const findKeyRecursive = (formula_applied, variable, position, isNotCustomVar = true) => {
      let propertyKey = undefined
      if (isNotCustomVar) {
        propertyKey = Object.keys(formula_applied).find((key) => key == `${position}_${variable}`)
      } else {
        propertyKey = Object.keys(formula_applied).find(
          (key) => key == variable && formula_applied[key].hasOwnProperty(`${position}_value`)
        )
      }
      if (propertyKey) {
        if (isNotCustomVar) {
          return formula_applied[propertyKey]
        } else {
          return formula_applied[propertyKey][`${position}_value`]
        }
      } else if (position > 0) {
        return findKeyRecursive(formula_applied, variable, position - 1, isNotCustomVar)
      } else {
        return null
      }
    }

    const findKeyInObject = (obj, keyToFind) => {
      let result = null

      const search = (currentObj) => {
        if (typeof currentObj !== 'object' || currentObj === null) return

        for (const key in currentObj) {
          if (key.includes(keyToFind)) {
            result = currentObj[key]
            return
          }
          search(currentObj[key])
          if (result !== null) break
        }
      }

      search(obj)
      return result
    }

    const isCustomVarVariableRecursive = (position, formula_applied, variable) => {
      if (formula_applied) {
        if ('var' in formula_applied) {
          let key_to_use = findKeyRecursive(formula_applied['var'], variable, position, false)
          if (key_to_use !== undefined && key_to_use !== null) {
            return key_to_use
          }
        }
      }
      return null
    }

    const isDataVarVariableRecursive = (position, formula_applied, variable) => {
      if (formula_applied) {
        const key = `${position}_${variable}`
        let key_to_use = findKeyInObject(formula_applied, key)
        if (key_to_use === null && position > 0) {
          key_to_use = isDataVarVariableRecursive(position - 1, formula_applied, variable)
        }
        return key_to_use
      }
    }

    const generateArray = (num) => {
      return Array.from({ length: num + 1 }, (_, i) => i)
    }

    const isCustomVarVariable = (position, variable, formula_applied, value) => {
      if (formula_applied && value === null) {
        if ('var' in formula_applied) {
          let pos = findPosition(position, generateArray(position))
          if (pos !== null) {
            let value_with_position = `${pos}_value`
            let valueFound = formula_applied['var'][variable]?.[value_with_position]
            if (valueFound !== undefined && valueFound !== null) {
              return valueFound
            }
          }
        }
      }
      return value
    }

    const isDataVarVariable = (position, variable, formula_applied, value) => {
      if (formula_applied && value === null) {
        let pos = findPosition(position, generateArray(position))
        if (pos !== null) {
          let var_with_position = `${pos}_${variable}`
          let valueFound = findKey(formula_applied, var_with_position)
          if (valueFound !== null && valueFound !== undefined) {
            return valueFound
          }
        }
      }
      return value
    }

    const updateAssignVariable = (index, formula, formula_applied) => {
      let left_expression = formula.children[0] // example : 'amount'
      let newValue = getValueOf(index, left_expression.label, formula_applied)
      if (newValue !== undefined || newValue !== null) {
        left_expression.value = newValue
      }
    }

    const hasValidChildrens = (formula) => (formula?.children?.length >= 2 ? true : false)

    const updateSearchVariable = (index, formula, formula_applied, value = null) => {
      if (!('children' in formula)) {
        if (value != null){
          formula.value = value
        }
      }
      let right_expression_zero = formula?.children?.[0]
      let right_expression_firts = formula?.children?.[1]
      if (right_expression_firts?.operator === 'search') {
        let newValue = getValueFromSearch(
          index,
          formula_applied,
          right_expression_firts.label,
          right_expression_firts.search[0] // here is the node 'var' or any different 'var'
        )
        if (newValue !== undefined && newValue !== null) {
          right_expression_firts.value = newValue
        }
      }
      if (right_expression_zero?.operator === 'search') {
        let newValue = getValueFromSearch(
          index,
          formula_applied,
          right_expression_zero.label,
          right_expression_zero.search[0] // here is the node 'var' or any different 'var'
        )
        if (newValue !== undefined && newValue !== null) {
          right_expression_zero.value = newValue
        }
      }

      if (hasValidChildrens(right_expression_firts)) {
        updateSearchVariable(index, right_expression_firts, formula_applied)
      }
      if (hasValidChildrens(right_expression_zero)) {
        updateSearchVariable(index, right_expression_zero, formula_applied)
      }
    }

    const updateValuesWithSolvedFormula = (formulas, formula_applied) => {
      let isValidFormula = (formulas) => (formulas && formulas.length >= 1 ? true : false)
      let isAssignOperator = (formula) => (formula?.operator === 'assign' ? true : false)
      if (isValidFormula(formulas)) {
        let newFormulas = formulas.map((formula, index) => {
          if (isAssignOperator(formula)) {
            if (hasValidChildrens(formula)) {
              updateAssignVariable(index, formula, formula_applied)
              if (index >= 1) {
                // it is not interesting to update the values ​​of the first structure since they do not change

                // pasamos formula.children[0]?.value para tomar el valor asignado, esto sirve para cuando del otro lado de la asignacion
                // hay una variable y no un operador ex: billing_amount = total_amount, en ese caso tomamos el valor de billing_amount
                // y lo mostramos en total_amount(ya que aca el valor viene cero)
                updateSearchVariable(
                  index - 1,
                  formula.children[1],
                  formula_applied,
                  formula.children[0]?.value
                )
              }
              return formula
            }
          }
        })
        return newFormulas
      }
    }

    const tranformStructure = (structure, parentId, parentOperator, lastId, operators, variablesFormat) => {
      let id = lastId
      const transformedStructure = structure
        .map((elem, index) => {
          const key = Object.keys(elem)[0]
          const operator = [...operators].find(
            (operatorAux) => operatorAux.operator === key && key !== 'search'
          )
          const variable = [...variablesFormat].find(
            (variableAux) =>
              variableAux.operator === key &&
              JSON.stringify(variableAux.search.map(String)) === JSON.stringify(elem.search.map(String))
          )

          if (operator || variable) {
            const obj = {
              ...(operator ? operator : variable),
              position: index + 1,
              id: id,
              isOperator: false,
            }
            if (parentId && parentOperator) {
              obj.parentId = parentId
              obj.parentOperator = parentOperator
            }

            id += 1

            if (key !== 'search' && operator) {
              const [children, lastIdChildren] = tranformStructure(
                [...elem[key]],
                obj.id,
                operator.operator,
                id,
                operators,
                variablesFormat
              )
              id = lastIdChildren
              obj.children = children
            } else {
              const search = elem[key]
              const isCreatedVar = search[0] === 'var' && search[2] === 'value'
              obj.search = search
              obj.created = isCreatedVar
              obj.value = variable.value
            }

            return obj
          }

          return null
        })
        .filter((elem) => elem)
      return [transformedStructure, id]
    }

    const checkInputValues = (array) => {
      let arrayAux = array.filter(
        (elem) => !elem.isData || (elem.isData && checkedBox.find((box) => box.label === elem.label))
      )
      arrayAux.forEach((elem) => {
        if (elem.children && elem.children.length) {
          elem.children = checkInputValues([...elem.children])
        }
      })
      return arrayAux
    }

    const getItem = (array, id) => {
      if (array && id) {
        let item = array.find((elem) => elem.id === id)
        if (!item) {
          const children = array.map((elem) => getItem(elem.children, id))
          item = children.find((elem) => elem)
        }
        return item
      }
      return null
    }

    const removeItemFromParent = (item) => {
      const formulasAux = [...formulas]
      if (item.parentId) {
        const parentItem = getItem(formulasAux, item.parentId)
        if (parentItem) {
          const index = parentItem.children.findIndex((elem) => elem.id === item.id)
          if (index > -1) {
            parentItem.children.splice(index, 1)
            delete item.parentId
          }
        }
      } else {
        const index = formulasAux.findIndex((elem) => elem.id === item.id)
        if (index > -1) {
          formulasAux.splice(index, 1)
          delete item.parentId
        }
      }

      return formulasAux
    }

    const isItemInFormula = (formula, item_id) => {
      if (formula?.length > 0) {
        let item = formula.find((elem) => elem.id === item_id)
        if (item) return false
        return true
      }
      return true
    }

    const checkAssignForms = (item, overId) => item.operator === 'assign' || overId !== 'forms'

    const checkAssignOperator = (item, overId) => item.operator !== 'assign' || !overId.includes('form_')

    const isValidPosition = (item, parentItem, position) => {
      const isEmpty = !parentItem?.children?.find((elem) => elem.position === position)
      const checkIfAssign = parentItem && parentItem.operator === 'assign' && (item.isData || position > 1)
      const isNotAssign = parentItem?.operator !== 'assign'

      return isEmpty && (checkIfAssign || isNotAssign)
    }

    const onDragCancel = useCallback(() => {
      setSelectedItem(null)
      setHighlight({})
      setDraggingAssign(false)
    }, [])

    const onDragStart = useCallback(
      ({ active }) => {
        const item = getItem([...operators, ...formulas, ...variablesFormat], active.id)
        if (item && 'assign' === item?.operator) {
          setDraggingAssign(true)
        }
        setSelectedItem(item)
      },
      [operators, formulas, variablesFormat]
    )

    const onDragOver = useCallback(
      ({ over }) => {
        const overId = over?.id
        const highlightAux = {}
        if (overId && typeof overId === 'string' && overId.startsWith('page_')) {
          const pageIndex = parseInt(overId.split('_')[1])
          handlePageChange(pageIndex)
          return
        }
        if (overId && selectedItem) {
          // if (typeof overId !== 'number' && overId.includes('form_') && selectedItem.operator === 'assign') {

          //   return
          // }
          let id = undefined
          let position = undefined
          if (typeof overId === 'number') {
            id = over?.id
          } else {
            const split = overId.split('_')
            id = parseInt(split[1])
            position = parseInt(split[2])
          }
          const parentItem = !isNaN(id) ? getItem(formulas, id) : null
          const isChildren = getItem(selectedItem.children, id)
          if (id === selectedItem.id || isChildren) {
            return
          }

          if (
            typeof overId !== 'number' &&
            checkAssignForms(selectedItem, overId) &&
            checkAssignOperator(selectedItem, overId) &&
            isValidPosition(selectedItem, parentItem, position)
          ) {
            highlightAux.valid = overId
          } else {
            highlightAux.not_valid = overId
          }
        }

        setHighlight(highlightAux)
      },
      [selectedItem, formulas, checkAssignForms, checkAssignOperator, isValidPosition]
    )

    // const onDragEnd = useCallback((event) => {
    //   const { active, over } = event
    //   const overId = over?.id
    //   if (overId && selectedItem) {
    //     if (
    //       typeof overId !== 'number' &&
    //       overId !== 'operators' &&
    //       overId !== 'data' &&
    //       checkAssignForms(selectedItem, overId) &&
    //       checkAssignOperator(selectedItem, overId)
    //     ) {
    //       let formulasAux = [...formulas]
    //       const split = overId.split('_')
    //       const id = parseInt(split[1])
    //       const position = parseInt(split[2])
    //       const parentItem = !isNaN(id) ? getItem(formulasAux, id) : null
    //       const isChildren = getItem(selectedItem.children, id)

    //       if (id !== selectedItem.id && !isChildren && isValidPosition(selectedItem, parentItem, position)) {
    //         let newItem
    //         if (selectedItem.isOperator) {
    //           const id = getNextIndex([], operators, variablesFormat, formulas)
    //           newItem = {
    //             ...selectedItem,
    //             id: id,
    //             isOperator: false,
    //           }
    //           if (!selectedItem.isData) {
    //             newItem.children = []
    //           }
    //         } else {
    //           newItem = { ...selectedItem }
    //           delete newItem.parentId
    //           delete newItem.position
    //           formulasAux = removeItemFromParent(selectedItem)
    //         }
    //         if (overId.includes('form_')) {
    //           newItem.parentId = id
    //           newItem.position = position
    //           newItem.parentOperator = parentItem.operator
    //           parentItem.children.push(newItem)
    //           setFormulas(formulasAux)
    //         } else {
    //           if (isItemInFormula(formulas, selectedItem?.id)) {
    //             // AVOID multirendering
    //             setFormulas([...formulasAux, newItem])
    //           }
    //         }
    //       }
    //     } else {
    //       const activeIndex = formulas.findIndex((item) => item.id === active.id)
    //       const overIndex = formulas.findIndex((item) => item.id === overId)
    //       if (activeIndex !== -1 && overIndex !== -1) {
    //         // Intercambia los elementos en la lista de fórmulas
    //         const newFormulas = [...formulas]
    //         const temp = newFormulas[activeIndex]
    //         newFormulas[activeIndex] = newFormulas[overIndex]
    //         newFormulas[overIndex] = temp

    //         // Actualiza el estado de las fórmulas
    //         setFormulas(newFormulas)
    //       }
    //     }
    //   }

    //   onDragCancel()
    // })

    const onDragEnd = useCallback(
      (event) => {
        const { active, over } = event
        const overId = over?.id
        if (overId && selectedItem) {
          if (
            typeof overId !== 'number' &&
            overId !== 'operators' &&
            overId !== 'data' &&
            checkAssignForms(selectedItem, overId) &&
            checkAssignOperator(selectedItem, overId)
          ) {
            let formulasAux = [...formulas]
            const split = overId.split('_')
            const id = parseInt(split[1])
            const position = parseInt(split[2])
            const parentItem = !isNaN(id) ? getItem(formulasAux, id) : null
            const isChildren = getItem(selectedItem.children, id)

            if (
              id !== selectedItem.id &&
              !isChildren &&
              isValidPosition(selectedItem, parentItem, position)
            ) {
              let newItem
              if (selectedItem.isOperator) {
                const newId = getNextIndex([], operators, variablesFormat, formulas)
                newItem = {
                  ...selectedItem,
                  id: newId,
                  isOperator: false,
                }
                if (!selectedItem.isData) {
                  newItem.children = []
                }
              } else {
                newItem = { ...selectedItem }
                delete newItem.parentId
                delete newItem.position
                formulasAux = removeItemFromParent(selectedItem)
              }

              if (overId.includes('form_')) {
                newItem.parentId = id
                newItem.position = position
                newItem.parentOperator = parentItem.operator
                parentItem.children.push(newItem)
                setFormulas(formulasAux)
              } else {
                if (isItemInFormula(formulas, selectedItem?.id)) {
                  // AVOID multirendering
                  setFormulas([...formulasAux, newItem])
                }
              }
            }
          } else {
            const activeIndex = formulas.findIndex((item) => item.id === active.id)
            const overIndex = formulas.findIndex((item) => item.id === overId)
            if (activeIndex !== -1 && overIndex !== -1) {
              const newFormulas = [...formulas]
              const movedItem = newFormulas.splice(activeIndex, 1)[0]
              newFormulas.splice(overIndex, 0, movedItem)
              setFormulas(newFormulas)
            }
          }
        }

        onDragCancel()
      },
      [
        formulas,
        selectedItem,
        operators,
        variablesFormat,
        checkAssignForms,
        checkAssignOperator,
        isValidPosition,
        onDragCancel,
      ]
    )

    const sortVariablesFormat = (variablesFormat) => {
      try {
        return variablesFormat.sort((a, b) => a.label.localeCompare(b.label))
      } catch (error) {
        return variablesFormat
      }
    }
    return (
      <DndContext
        modifiers={[restrictToWindowEdges, snapCenterToCursor]}
        collisionDetection={pointerWithin}
        sensors={sensors}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <Box style={{ display: 'flex', width: '100%', margin: '0px 15px' }}>
          {!withoutVars && (
            <Box style={{ width: '25%' }}>
              {!onlyView && (
                // <Box>
                //   {translate('ra.field.operators')}
                //   <Box style={{ width: '100%' }}>
                //     <Droppable id='operators' items={operators} onlyView={onlyView} />
                //   </Box>
                // </Box>

                <CollapsibleSection
                  title={translate('ra.field.operators')}
                  isOpen={showOperators}
                  onToggle={() => setShowOperators(!showOperators)}
                >
                  <Box style={{ width: '100%' }}>
                    <Droppable
                      id='operators'
                      items={operators}
                      onlyView={onlyView}
                      selectedRuleType={selectedRuleType}
                      ruleLiquidationTypes={ruleLiquidationTypes}
                    />
                  </Box>
                </CollapsibleSection>
              )}

              <CollapsibleSection
                title={translate('ra.field.variables')}
                isOpen={showVariables}
                onToggle={() => setShowVariables(!showVariables)}
              >
                <Box>
                  {loading ? (
                    [...Array(6)].map((e, i) => (
                      <div
                        style={{
                          margin: '0px 10px 0px 10px',
                          width: i % 3 === 0 ? '17%' : '23%',
                          height: '52px',
                        }}
                        key={i}
                      >
                        <Skeleton
                          variant={undefined}
                          width={'100%'}
                          height={'100%'}
                          style={{ borderRadius: '16px' }}
                        />
                      </div>
                    ))
                  ) : (
                    <Droppable
                      id='data'
                      items={sortVariablesFormat(variablesFormat)}
                      onlyView={onlyView}
                      varList={true}
                      classCall={undefined}
                      selectedRuleType={selectedRuleType}
                      ruleLiquidationTypes={ruleLiquidationTypes}
                    />
                  )}
                </Box>
              </CollapsibleSection>

              {selectedRuleType === LIQUIDATION && (
                <CollapsibleSection
                  title={translate('ra.title.color_reference')}
                  isOpen={showLegend}
                  onToggle={() => setShowLegend(!showLegend)}
                >
                  {COLORS
                    .filter(color => !color.showIf || color.showIf.includes(selectedRuleType))
                    .map((color, i) => (
                      <Box key={i} style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '5px 0' }}>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            backgroundColor: color.color,
                            border: '1px solid #BDBDBD'
                          }}
                        />
                        <Typography style={{  }}>
                          {translate(color.text)}
                        </Typography>
                      </Box>
                    ))
                  }
                  <Box style={{ display: 'flex', gap: '10px', alignItems: 'center', margin: '5px 0' }}>
                    <Box
                      style={{
                        width: '16px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontWeight: 'bold',
                        fontSize: '11px'
                      }}
                    >
                      [VG]
                    </Box>
                    <Typography>
                      {translate('ra.legend.global_variable_indicator')}
                    </Typography>
                  </Box>
                </CollapsibleSection>
              )}
            </Box>
          )}

          <Box style={{ width: withoutVars ? '100%' : '75%' }}>
            {!onlyView && (
              <>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box display='flex' justifyContent='space-between' alignItems='center'>
                    <Box>{translate('ra.title.formula')}</Box>
                    <Tooltip title='Más información'>
                      <IconButton onClick={handleTooltipClick}>
                        <InfoIcon color='info' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <div style={{display:'flex', flexDirection:'row'}}>
                    <Button
                      variant='contained'
                      style={{marginRight:'15px'}}
                      onClick={()=>{
                        try{
                          const objToCopy = {formulas, jsonParse, checkedBox}
                          navigator.clipboard.writeText(JSON.stringify(objToCopy))
                          notify('ra.notification.formula_copy_success',{type:'success'})
                        } catch {
                          notify('ra.notification.formula_copy_error',{type:'error'})
                        }
                        
                      }}
                    >
                      {translate('ra.action.copy_formula')}
                    </Button>
                    <Button
                      variant='contained'
                      onClick={async ()=>{
                        setOpenjsonFormula(true)
                        /* try{
                          const pasteValue = await navigator.clipboard.readText()
                          const jsonPasteValue = await JSON.parse(pasteValue)
                          setPastedFormula(jsonPasteValue)
                        }catch{
                          notify('ra.notification.formula_paste_error',{type:'error'})
                          setPastedFormula(null)
                        } */
                      }}
                    >
                      {translate('ra.action.paste_formula')}
                    </Button>
                  </div>
                  <Box>
                    <IconButton onClick={toggleSize} disabled={!isSmall}>
                      <AddIcon />
                    </IconButton>
                    <IconButton onClick={toggleSize} disabled={isSmall}>
                      <RemoveIcon />
                    </IconButton>
                  </Box>
                </Box>
                {showAlert && (
                  <Box mt={2}>
                    <Alert severity='info'>{translate('ra.message.rule_formula_explantion')}</Alert>
                  </Box>
                )}
              </>
            )}
            {onlyView && !withoutVars && translate('ra.field.formula')}
            {withoutVars && translate('ra.title.solve_formula')}
            <Box
              className={classNames('sortable_list', {
                highlight: highlight.valid === 'forms',
                highlight_not_valid: highlight.not_valid === 'forms',
              })}
              style={{
                padding: '0px 10px 10px',
                border: '2px solid lightgray',
                borderRadius: '20px',
                minHeight: '300px',
              }}
            >
              {loading ? (
                [...Array(3)].map((e, i) => (
                  <div style={{ width: i % 3 == 0 ? '60%' : '73%', height: '120px' }} key={i}>
                    <Skeleton
                      width={'100%'}
                      height={'100%'}
                      style={{ borderRadius: '20px', margin: 0, padding: 0 }}
                    />
                  </div>
                ))
              ) : (
                <DraggingAssignContext.Provider value={{ draggingAssign, setDraggingAssign, isSmall }}>
                  <DroppableSortable
                    id='forms'
                    items={itemsToShow}
                    onlyView={onlyView}
                    withoutValues={withoutValues}
                    vertical
                    formulaLength={formulas.length}
                    highlight={highlight}
                    selectedRuleType={selectedRuleType}
                    ruleLiquidationTypes={ruleLiquidationTypes}
                    onClickRemove={(item) => setFormulas(removeItemFromParent(item))}
                  />
                </DraggingAssignContext.Provider>
              )}
            </Box>
            {/* Paginador */}
            <Box display='flex' justifyContent='center' mt={2}>
              <FormControl
                variant='outlined'
                size='small'
                sx={{ minWidth: 120, margin: '0 5px', padding: '5px' }}
              >
                <Select value={itemsPerPage} onChange={handleItemsPerPageChange} label={''}>
                  {[3, 5, 7, 10, 20, 30, 50, 100].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {[...Array(totalPages)].map((_, pageIndex) => (
                <Box
                  key={pageIndex}
                  id={`page_${pageIndex}`}
                  onMouseEnter={() => selectedItem && handlePageChange(pageIndex)}
                  onClick={() => handlePageChange(pageIndex)}
                  sx={{ margin: '0 5px', padding: '5px', cursor: 'pointer' }}
                >
                  <Button variant={currentPage === pageIndex ? 'contained' : 'outlined'}>
                    {pageIndex + 1}
                  </Button>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </DndContext>
    )
  }
)
